'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import { logoutService } from '@/lib/auth/ory/service';
import {
  OAuth2ApiError,
  acceptLoginRequest,
  getLoginRequest,
  isSafeRedirectTo,
  mapLoginFlow,
  resolveLoginFlow,
  waitForMinLoaderVisible,
} from '@/lib/oauth2';
import type { LoginFlowDecision, OAuth2ClientSummary } from '@/lib/oauth2';
import { navigateToUrl } from '@/utils/navigateToUrl';

import { LoginConfirmIdentityCard } from '../-components/LoginConfirmIdentityCard';
import { LoginWrongAccountCard } from '../-components/LoginWrongAccountCard';
import { OAuth2ErrorCard } from '../-components/OAuth2ErrorCard';
import { OAuth2ProgressScreen } from '../-components/OAuth2ProgressScreen';
import { OAuth2Screen } from '../-components/OAuth2Screen';

const SearchSchema = z.object({
  /** Hydra から渡される login challenge */
  login_challenge: z.string().min(1).optional(),
});

export const Route = createFileRoute('/oauth2/login/')({
  validateSearch: zodValidator(SearchSchema),
  component: OAuth2LoginPage,
});

type RenderDecision = Extract<LoginFlowDecision, { kind: 'render' }>;

// effect の依存に含めないようモジュールスコープに置く（window.location のみ参照）。
const goToSignIn = () => {
  const currentUrl = window.location.pathname + window.location.search;
  navigateToUrl(`/auth/sign-in?redirect=${encodeURIComponent(currentUrl)}`, {
    replace: true,
  });
};

/** 判定と Hydra accept は BE が実施。FE は resolveLoginFlow の action/reason に従う。 */
function OAuth2LoginPage() {
  const { login_challenge } = Route.useSearch();
  const [client, setClient] = useState<OAuth2ClientSummary | null>(null);
  const [renderDecision, setRenderDecision] = useState<RenderDecision | null>(
    null,
  );
  const [asyncError, setAsyncError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const missingChallengeError = !login_challenge
    ? new Error('missing_login_challenge')
    : null;
  const error = missingChallengeError ?? asyncError;
  const challenge = login_challenge ?? '';

  const handleRedirect = (redirectTo: string) => {
    if (!isSafeRedirectTo(redirectTo)) {
      setAsyncError(new Error('unsafe_redirect_to'));
      return;
    }
    navigateToUrl(redirectTo, { replace: true });
  };

  useEffect(() => {
    if (!login_challenge) return;
    let cancelled = false;
    void (async () => {
      const startedAt = performance.now();
      try {
        // 案1: resolve を先に呼び、待機中もアプリ名を表示する
        const branding = await getLoginRequest(login_challenge);
        if (cancelled) return;
        setClient(branding.client);

        const decision = mapLoginFlow(await resolveLoginFlow(login_challenge));
        if (cancelled) return;

        if (decision.kind === 'redirect') {
          if (!isSafeRedirectTo(decision.redirectTo)) {
            throw new Error('unsafe_redirect_to');
          }
          await waitForMinLoaderVisible(startedAt);
          if (cancelled) return;
          navigateToUrl(decision.redirectTo, { replace: true });
          return;
        }

        if (decision.reason === 'sign_in') {
          goToSignIn();
          return;
        }
        if (
          decision.reason === 'confirm_identity' &&
          !decision.currentIdentity?.email
        ) {
          // 本人確認できないため sign-in にフォールバック
          goToSignIn();
          return;
        }
        setRenderDecision(decision);
      } catch (e) {
        if (cancelled) return;
        setAsyncError(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [login_challenge]);

  const handleContinue = async () => {
    setSubmitting(true);
    try {
      const { redirectTo } = await acceptLoginRequest({ challenge });
      handleRedirect(redirectTo);
    } catch (e) {
      // 成功時は遷移するためローディングを維持し、失敗時のみ解除（二重送信防止）。
      setAsyncError(e);
      setSubmitting(false);
    }
  };

  const handleUseAnotherAccount = async () => {
    setSubmitting(true);
    // Kratos セッションを破棄（失敗しても sign-in へは進む）
    try {
      const flow = await logoutService.initFlow();
      if (flow.logout_token) {
        await logoutService.submitFlow(flow.logout_token);
      }
    } catch {
      // sign-in 誘導は継続
    }
    // goToSignIn は必ず遷移するため submitting は解除しない（二重送信防止）。
    goToSignIn();
  };

  if (error) {
    const fallbackKey =
      error instanceof OAuth2ApiError ? undefined : 'invalidRequest';
    return (
      <OAuth2Screen>
        <OAuth2ErrorCard error={error} fallbackKey={fallbackKey} />
      </OAuth2Screen>
    );
  }

  if (renderDecision?.reason === 'confirm_identity') {
    // 不変条件: ここに到達する時点で email は非 null（effect が保証）。
    // 欠落時は空表示を出さず、フォールスルーしてローダーを表示する。
    const email = renderDecision.currentIdentity?.email;
    if (email) {
      return (
        <OAuth2Screen>
          <LoginConfirmIdentityCard
            client={renderDecision.client}
            email={email}
            requestedScope={renderDecision.requestedScope}
            correlationId={renderDecision.correlationId}
            onContinue={() => void handleContinue()}
            onUseAnotherAccount={() => void handleUseAnotherAccount()}
            submitting={submitting}
          />
        </OAuth2Screen>
      );
    }
  }

  if (renderDecision?.reason === 'wrong_account') {
    return (
      <OAuth2Screen>
        <LoginWrongAccountCard
          client={renderDecision.client}
          email={renderDecision.currentIdentity?.email ?? null}
          correlationId={renderDecision.correlationId}
          onUseAnotherAccount={() => void handleUseAnotherAccount()}
          submitting={submitting}
        />
      </OAuth2Screen>
    );
  }

  return (
    <OAuth2Screen>
      <OAuth2ProgressScreen client={client} />
    </OAuth2Screen>
  );
}
